import {AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, Output, ViewChild} from '@angular/core';
import {MatExpansionPanel, MatExpansionPanelContent, MatExpansionPanelHeader, MatExpansionPanelState} from '@angular/material/expansion';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgClass} from '@angular/common';

@Component({
  selector: 'mx-expandable-card',
  templateUrl: './expandable-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelContent, NgClass]
})
export class ExpandableCardComponent implements AfterViewInit {

  @ViewChild('matExpansionPanel') expansionPanel: MatExpansionPanel;

  /** If the content handles its own lazy loading, set this to `false` so that this component won't interfere. */
  @Input() public lazyLoadContent: boolean = true;

  /**
   * It probably doesn't make much sense to set this without a sticky main page header.
   * Troubleshooting?  If setting this doesn't make the header stick, it's most likely because some ancestor has overflow hidden.
   */
  @Input() public stickyHeader: boolean = false;

  /**
   * Most likely a `top-<something>` class, to tell the header where to stick.  This is configurable because the correct value probably
   * depends on the height of the sticky main page header.
   *
   * This is applied whether `stickyHeader` is `true` or not -- perhaps unhelpfully. So you probably shouldn't set this without also setting
   * `stickyHeader` to `true`.
   */
  @Input() public stickyHeaderPositionCssClass: string = '';

  /** When `true`, this separates the card's header and content with a border and some extra margin.  Applies only when the card is expanded. */
  @Input() public showHeaderBottomBorder: boolean = false;

  /** Override classes for the Expansion Panel */
  @Input() public expansionPanelClasses: string = '';

  @Input()
  public set expansionState(state: MatExpansionPanelState) { // Not just a simple input because we can update the private property internally.
    if (this._expansionState === state) {
      // Cancel any stale intent that no longer makes sense.
      this.pendingIntendedExpansionState = undefined;
      return;
    }

    this._expansionState = state;
    this.pendingIntendedExpansionState = state;

    if (this.expansionPanel) {
      if (state === 'expanded') {
        this.expansionPanel.open();
      } else {
        this.expansionPanel.close();
      }
    }

    this.isAnimating = true;
  }

  public get expansionState(): MatExpansionPanelState {
    return this._expansionState;
  }

  @Output() public readonly expanded: EventEmitter<void> = new EventEmitter<void>();
  @Output() public readonly collapsed: EventEmitter<void> = new EventEmitter<void>();
  @Output() public readonly expansionStateChanged: EventEmitter<MatExpansionPanelState> = new EventEmitter();

  public isAnimating: boolean = false;

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private _expansionState: MatExpansionPanelState = 'collapsed';

  // This ensures that we act only on "real" expansion state changes -- specifically excluding the counteractive toggle in handleHeaderInteraction().
  private pendingIntendedExpansionState?: MatExpansionPanelState;

  public ngAfterViewInit(): void {
    this.expansionPanel.afterExpand
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._expansionState = 'expanded';
        this.isAnimating = false;

        if (this.pendingIntendedExpansionState === 'expanded') {
          this.expanded.emit();
          this.expansionStateChanged.emit('expanded');
          this.pendingIntendedExpansionState = undefined;
        }
      });

    this.expansionPanel.afterCollapse
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._expansionState = 'collapsed';
        this.isAnimating = false;

        if (this.pendingIntendedExpansionState === 'collapsed') {
          this.collapsed.emit();
          this.expansionStateChanged.emit('collapsed');
          this.pendingIntendedExpansionState = undefined;
        }
      });

    // Initialize the panel to our first state, just in case we start out of sync.
    if (this._expansionState === 'expanded') {
      this.expansionPanel.open();
    } else {
      this.expansionPanel.close();
    }
  }

  public get isExpanded(): boolean {
    return this._expansionState === 'expanded';
  }

  public onClickHeader(event: Event, expansionPanel: MatExpansionPanel): void {
    this.handleHeaderInteraction(event, expansionPanel);
  }

  public onKeydownEnter(event: Event, expansionPanel: MatExpansionPanel): void {
    this.handleHeaderInteraction(event, expansionPanel);
  }

  private handleHeaderInteraction(event: Event, expansionPanel: MatExpansionPanel): void {
    /*
      MatExpansionPanel toggles its expansion state via any click on the header (which is a fine default).  But it has no way to disable that functionality
      (which is silly).  We want to be able to hide their default toggle button (which they do let us do), replace it with our own (which might not be in the
      same place theirs was), and maybe also add other buttons nearby.  Thus, we want to toggle expansion only when the header is clicked, or our own
      expansion button is clicked, but not when any other header button is clicked.

      So this handler simply toggles the expansion state when a non-expansion button is clicked, which counteracts the built-in toggle that happened when
      the button was clicked.  Luckily, there is no perceptible delay between these two toggle requests, so clicking those additional buttons (in effect) does
      nothing in this context.

      However... if the user clicks our own expansion toggle button (or anywhere else in the header), we go ahead and let the built-in toggle happen.  It's kind
      of a happy accident that we don't need to purposely wire up our own expansion button to do the toggling -- it just happens because it's in the header.

      By the same reasoning, we ignore all of Angular Material's built-in expansion state change events and create our own based solely on direct toggle button
      interaction.  Otherwise, we'd get unexpected "expanded" events even when we were immediately toggling the panel closed again.  Etc.

      Thanks to https://github.com/angular/components/issues/8190#issuecomment-344769956.
    */

    event.stopPropagation();
    this.isAnimating = true;

    if (this.isANonExpansionToggleButton(event.target as Element)) {
      // A button other than our toggle button was clicked.  Counteract the automatic toggle to keep the panel in the same state.
      expansionPanel.toggle();
      // We don't set this.pendingIntendedExpansionState here since we're just undoing the toggle, so outputs won't emit.
    } else {
      // The expansion toggle button or anything else in the header was clicked, so let the toggle happen, and we'll catch the afterExpand/afterCollapse event.
      this.pendingIntendedExpansionState = this.isExpanded ? 'collapsed' : 'expanded'; // This will let our outputs emit.
    }
  }

  private isANonExpansionToggleButton(element: Element | null): boolean {
    return !!element?.closest('.mat-mdc-button-base') && !element?.closest('mx-expandable-card-toggle-button');
  }
}
